
import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove } from 'firebase/database';
import { db } from '../../firebase';
import { CargoCompany } from '../../types';
import { Plus, Trash2, X, Check, Truck, Globe, ExternalLink } from 'lucide-react';

const AdminCargo: React.FC = () => {
  const [companies, setCompanies] = useState<CargoCompany[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', trackingUrl: '' });

  useEffect(() => {
    const cargoRef = ref(db, 'cargoCompanies');
    onValue(cargoRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCompanies(Object.values(data));
      } else {
        setCompanies([]);
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newRef = push(ref(db, 'cargoCompanies'));
    await set(newRef, { ...formData, id: newRef.key });
    setIsModalOpen(false);
    setFormData({ name: '', trackingUrl: '' });
  };

  const deleteCompany = (id: string) => {
    if (window.confirm('Bu kargo firmasını silmek istediğinize emin misiniz?')) {
      remove(ref(db, `cargoCompanies/${id}`));
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Kargo Yönetimi</h1>
          <p className="text-stone-500 font-medium italic mt-1">Sistemdeki kargo firmalarını ve takip linklerini yönetin.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-amber-800 text-white px-6 py-3 rounded-2xl font-bold flex items-center space-x-2 shadow-lg shadow-amber-900/20"
        >
          <Plus size={20} />
          <span>Firma Ekle</span>
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map((comp) => (
          <div key={comp.id} className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm group hover:border-amber-200 transition-all">
            <div className="flex items-center justify-between mb-6">
              <div className="w-14 h-14 bg-stone-50 rounded-2xl flex items-center justify-center text-amber-800 group-hover:bg-amber-50 transition-colors">
                <Truck size={28} />
              </div>
              <button 
                onClick={() => deleteCompany(comp.id)}
                className="p-2 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
              >
                <Trash2 size={20} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2">{comp.name}</h3>
            <div className="flex items-center text-xs text-stone-400 font-mono break-all line-clamp-2">
              <Globe size={14} className="mr-1 flex-shrink-0" />
              {comp.trackingUrl}
            </div>
          </div>
        ))}

        {companies.length === 0 && (
          <div className="col-span-full py-20 bg-stone-50 border-2 border-dashed border-stone-200 rounded-[3rem] text-center text-stone-400">
            <Truck size={48} className="mx-auto mb-4 opacity-20" />
            <p className="italic font-medium">Henüz bir kargo firması eklenmemiş.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between bg-stone-50">
              <h2 className="text-xl font-black text-stone-900">Yeni Kargo Firması</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-stone-400 hover:text-stone-600"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Firma Adı</label>
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                  placeholder="Örn: Aras Kargo"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Sorgu URL'si</label>
                <div className="space-y-3">
                  <input 
                    type="url" required
                    value={formData.trackingUrl}
                    onChange={(e) => setFormData({...formData, trackingUrl: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                    placeholder="https://sorgu.aras.com?code="
                  />
                  <div className="flex items-start space-x-2 p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <ExternalLink size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-amber-800 leading-relaxed font-bold italic">
                      Takip numarasının URL'nin sonuna ekleneceğini unutmayın. Örn: <b>...aspx?code=</b>
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button 
                  type="submit"
                  className="w-full bg-amber-800 text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-amber-900 transition-all shadow-xl shadow-amber-900/20"
                >
                  <Check size={20} />
                  <span>Firmayı Kaydet</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCargo;
